/**
 * Protected route wrapper that requires a usable Auth0 session.
 *
 * `isAuthenticated` from the Auth0 SDK only means a user profile is cached in
 * localStorage. It stays true after the access token has expired, even when
 * the SDK can no longer renew it: the API issues no refresh tokens, and the
 * SDK's own startup check swallows the failure. Rendering the app in that
 * state produced an empty shell (no /auth/me call, every API request failing)
 * that the user could only leave by signing out and back in.
 *
 * So before rendering children this component asks the SDK for a token once.
 * When that fails, or when a request later gets a 401, it sends the browser
 * back through Auth0's /authorize with the current location as `returnTo`.
 * The Auth0 session cookie normally makes that round trip invisible and the
 * user lands back where they were; only when that session is gone too does
 * Auth0 show the login page. A loop guard (see sessionRecovery.ts) turns a
 * repeated failure right after re-auth into a full logout.
 */

import { useAuth0 } from "@auth0/auth0-react";
import type { ReactNode } from "react";
import { useCallback, useEffect, useRef, useState } from "react";

import { Loader } from "@/ui/atoms";

import { getAuth0Config } from "./config";
import {
    clearSilentReauthAttempt,
    currentReturnTo,
    describeAuthError,
    markSilentReauthAttempt,
    shouldAttemptSilentReauth,
} from "./sessionRecovery";
import { tokenService } from "./tokenService";

interface TProtectedRouteProps {
    children: ReactNode;
}

type TSessionStatus = "checking" | "ready";

function FullScreenLoader() {
    return (
        <div className="flex h-screen items-center justify-center">
            <Loader size="lg" />
        </div>
    );
}

export function ProtectedRoute({ children }: TProtectedRouteProps) {
    const { isAuthenticated, isLoading, loginWithRedirect, getAccessTokenSilently, logout } =
        useAuth0();
    const config = getAuth0Config();
    const [sessionStatus, setSessionStatus] = useState<TSessionStatus>("checking");
    // Only one recovery redirect per page load, however many requests fail.
    const recovering = useRef(false);

    // Called when the cached session cannot produce a token (expired and not
    // renewable) or when the API rejects the token with a 401.
    const recoverSession = useCallback(
        (reason: string) => {
            if (recovering.current) {
                return;
            }
            recovering.current = true;

            if (shouldAttemptSilentReauth()) {
                console.warn(`Auth session unusable (${reason}); re-authenticating via Auth0`);
                markSilentReauthAttempt();
                void loginWithRedirect({ appState: { returnTo: currentReturnTo() } });
                return;
            }

            console.warn(`Auth session still unusable after re-auth (${reason}); logging out`);
            clearSilentReauthAttempt();
            void logout({ logoutParams: { returnTo: window.location.origin } });
        },
        [loginWithRedirect, logout],
    );

    // Not signed in at all: go to Auth0 login and come back to this page.
    useEffect(() => {
        if (isLoading || isAuthenticated) {
            return;
        }
        void loginWithRedirect({ appState: { returnTo: currentReturnTo() } });
    }, [isLoading, isAuthenticated, loginWithRedirect]);

    // Signed in according to the cache: confirm a token is actually obtainable
    // before rendering anything. A cached, unexpired token resolves instantly.
    useEffect(() => {
        if (isLoading || !isAuthenticated) {
            return;
        }
        let cancelled = false;
        getAccessTokenSilently({ authorizationParams: { audience: config.audience } })
            .then(() => {
                if (!cancelled) {
                    setSessionStatus("ready");
                }
            })
            .catch((err: unknown) => {
                if (!cancelled) {
                    recoverSession(describeAuthError(err));
                }
            });
        return () => {
            cancelled = true;
        };
    }, [isLoading, isAuthenticated, getAccessTokenSilently, config.audience, recoverSession]);

    // Set up the token getter and auth error handler for the API client.
    // This allows the API interceptor to get fresh tokens on every request
    // and handle auth failures by re-authenticating.
    useEffect(() => {
        if (!isAuthenticated) {
            tokenService.clearTokenGetter();
            tokenService.clearAuthErrorHandler();
            return;
        }

        tokenService.setTokenGetter(async () => {
            try {
                return await getAccessTokenSilently({
                    authorizationParams: { audience: config.audience },
                });
            } catch (err) {
                recoverSession(`token refresh failed: ${describeAuthError(err)}`);
                return null;
            }
        });
        tokenService.setAuthErrorHandler(() => recoverSession("API returned 401"));

        return () => {
            tokenService.clearAuthErrorHandler();
        };
    }, [isAuthenticated, getAccessTokenSilently, config.audience, recoverSession]);

    if (isLoading || !isAuthenticated || sessionStatus !== "ready") {
        return <FullScreenLoader />;
    }

    return <>{children}</>;
}
